import { ContractType } from "@/Overtime/enums/contract"
import type { LiquidityPoolCollateral } from "@/Overtime/enums/liquidityPool"
import type { NetworkConfig } from "@/Overtime/types/network"
import type { ContractData, ViemContract } from "@/Overtime/types/viem"
import { type Address, getContract } from "viem"
import { getCollaterals } from "./collaterals"
// Contract import
import { TBD_ADDRESS } from "@/Overtime/constants/network"
import freeBetHolder from "@/Overtime/utils/contracts/freeBetHolder"
import liquidityPoolDataContract from "@/Overtime/utils/contracts/liquidityPoolDataContractV2"
import liveTradingProcessor from "@/Overtime/utils/contracts/liveTradingProcessorContract"
import liveTradingProcessorData from "@/Overtime/utils/contracts/liveTradingProcessorDataContract"
import multiCall from "@/Overtime/utils/contracts/multiCallContract"
import multiCollateralOnOffRamp from "@/Overtime/utils/contracts/multiCollateralOnOffRampContract"
import multiCollateral from "@/Overtime/utils/contracts/multipleCollateralContract"
import priceFeed from "@/Overtime/utils/contracts/priceFeedContract"
import sgpTradingProcessor from "@/Overtime/utils/contracts/sgpTradingProcessorContract"
import sportsAMMData from "@/Overtime/utils/contracts/sportsAMMDataContract"
import sportsAMMV2 from "@/Overtime/utils/contracts/sportsAMMV2Contract"
import sportsAMMV2Manager from "@/Overtime/utils/contracts/sportsAMMV2ManagerContract"
import sportsAMMV2ResultManager from "@/Overtime/utils/contracts/sportsAMMV2ResultManagerContract"
import sportsAMMV2RiskManager from "@/Overtime/utils/contracts/sportsAMMV2RiskManagerContract"
import stakingThalesBettingProxy from "@/Overtime/utils/contracts/stakingThalesBettingProxy"
import liquidityPoolContractV2 from "./contracts/liquidityPoolContractV2"
import resolveBlockerContract from "./contracts/resolveBlockerContract"
import thalesContract from "./contracts/thalesContract"
import thalesToOverMigrationContract from "./contracts/thalesToOverMigrationContract"

const prepareContractWithModifiedResponse = (props: {
  abi: any
  address: Address
  client: any
}) => {
  const contract = getContract(props) as ViemContract

  if (typeof contract.read !== "object" || contract.read === null) {
    throw new Error("contract.read must be an object")
  }

  return {
    ...contract,
    read: new Proxy(contract.read, {
      get: (_, functionName) => async (args: any) => {
        const functionABI = props.abi.find(
          (abiFraction: any) => abiFraction.name === functionName
        )
        const functionResponse: any[] = await contract.read[functionName](args)

        const modifiedResponse = [] as any[]

        if (!Array.isArray(functionResponse)) {
          modifiedResponse[0] = functionResponse
        } else if (
          typeof functionResponse === "object" &&
          functionResponse !== null
        ) {
          Object.keys(functionResponse).forEach((key: any) => {
            ;(modifiedResponse as any)[key] = functionResponse[key]
          })
        } else {
          modifiedResponse.push(...(functionResponse as any[]))
        }

        if (functionABI) {
          functionABI.outputs.forEach((output: any, index: number) => {
            if (output.name === "") {
              return
            }
            modifiedResponse[output.name] = functionResponse[index]
          })
        }

        return modifiedResponse?.length === 1
          ? modifiedResponse[0]
          : modifiedResponse
      },
    }),
  }
}

const getContractWithModifiedResponse = (
  contractData: ContractData,
  networkConfig: NetworkConfig
) => {
  if (contractData.addresses[networkConfig?.networkId] === TBD_ADDRESS) {
    return undefined
  }
  if (contractData.addresses[networkConfig?.networkId] === TBD_ADDRESS) {
    return undefined
  }
  return prepareContractWithModifiedResponse({
    abi: contractData.abi,
    address: contractData.addresses[networkConfig?.networkId],
    client: networkConfig?.client,
  }) as ViemContract
}

export const getContractInstance = (
  contractName: string,
  networkConfig: NetworkConfig,
  selectedToken?: number,
  lpCollateral?: LiquidityPoolCollateral
) => {
  switch (contractName) {
    case ContractType.LIQUIDITY_POOL_DATA:
      return getContractWithModifiedResponse(
        liquidityPoolDataContract,
        networkConfig
      )
    case ContractType.PRICE_FEED:
      return getContractWithModifiedResponse(priceFeed, networkConfig)
    case ContractType.MULTICOLLATERAL:
      if (selectedToken == undefined || selectedToken == -1) {
        return
      }
      return getContractWithModifiedResponse(
        multiCollateral[getCollaterals(networkConfig.networkId)[selectedToken]],
        networkConfig
      )
    case ContractType.MULTICOLLATERAL_ON_OFF_RAMP:
      return getContractWithModifiedResponse(
        multiCollateralOnOffRamp,
        networkConfig
      )
    case ContractType.SPORTS_AMM_DATA:
      return getContractWithModifiedResponse(sportsAMMData, networkConfig)
    case ContractType.SPORTS_AMM_V2:
      return getContractWithModifiedResponse(sportsAMMV2, networkConfig)
    case ContractType.SPORTS_AMM_V2_RISK_MANAGER:
      return getContractWithModifiedResponse(
        sportsAMMV2RiskManager,
        networkConfig
      )
    case ContractType.SPORTS_AMM_V2_RESULT_MANAGER:
      return getContractWithModifiedResponse(
        sportsAMMV2ResultManager,
        networkConfig
      )
    case ContractType.LIVE_TRADING_PROCESSOR:
      return getContractWithModifiedResponse(
        liveTradingProcessor,
        networkConfig
      )
    case ContractType.LIVE_TRADING_PROCESSOR_DATA:
      return getContractWithModifiedResponse(
        liveTradingProcessorData,
        networkConfig
      )
    case ContractType.SGP_TRADING_PROCESSOR:
      return getContractWithModifiedResponse(sgpTradingProcessor, networkConfig)
    case ContractType.FREE_BET_HOLDER:
      return getContractWithModifiedResponse(freeBetHolder, networkConfig)
    case ContractType.SPORTS_AMM_V2_MANAGER:
      return getContractWithModifiedResponse(sportsAMMV2Manager, networkConfig)
    case ContractType.MULTICALL:
      return getContractWithModifiedResponse(multiCall, networkConfig)
    case ContractType.STAKING_THALES_BETTING_PROXY:
      return getContractWithModifiedResponse(
        stakingThalesBettingProxy,
        networkConfig
      )
    case ContractType.LIQUIDITY_POOL:
      if (!lpCollateral) {
        return
      }
      return getContractWithModifiedResponse(
        liquidityPoolContractV2[lpCollateral],
        networkConfig
      )
    case ContractType.RESOLVE_BLOCKER:
      return getContractWithModifiedResponse(
        resolveBlockerContract,
        networkConfig
      )
    case ContractType.THALES_TO_OVER_MIGRATION:
      return getContractWithModifiedResponse(
        thalesToOverMigrationContract,
        networkConfig
      )
    case ContractType.THALES:
      return getContractWithModifiedResponse(thalesContract, networkConfig)
    default:
      return undefined
  }
}
